import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { api } from '../lib/api'
import { getDateLocale, getLocalizedField, useSiteLang } from '../lib/localizedContent'
import { resolveMediaUrl } from '../lib/media'
import SEO from '../components/SEO'
import { buildCanonicalUrl } from '../lib/site'
import {
  ArrowLeft,
  Building2,
  Calendar,
  FolderKanban,
  Send,
  Tag,
} from 'lucide-react'

interface Project {
  id: string
  title: string
  titleEN: string
  titleRU: string
  description: string
  descriptionEN: string
  descriptionRU: string
  imageUrl?: string
  industry_type?: string
  industry_typeRU?: string
  industry_typeEN?: string
  clientName?: string
  completedDate?: string
  isActive: boolean
}

const ProjectsView = () => {
  const { id } = useParams<{ id: string }>()
  const { t, i18n } = useTranslation()
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const lang = useSiteLang()
  const dateLocale = getDateLocale(i18n.resolvedLanguage ?? i18n.language)

  useEffect(() => {
    if (!id) return

    const loadProject = async () => {
      try {
        const data = await api.projects.getById(id)
        if (!data.isActive) {
          setError('not_found')
          return
        }
        setProject(data)
      } catch (err) {
        console.error('Error loading project:', err)
        setError('not_found')
      } finally {
        setLoading(false)
      }
    }

    loadProject()
  }, [id])

  if (loading) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <FolderKanban className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg mb-6">{t('projects.notFound')}</p>
          <Link to="/projects" className="btn-primary inline-flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('projects.back')}
          </Link>
        </div>
      </div>
    )
  }

  const title = getLocalizedField(project, 'title', lang)
  const description = getLocalizedField(project, 'description', lang)
  const industryType = getLocalizedField(project, 'industry_type', lang)
  const imageSrc = resolveMediaUrl(project.imageUrl)

  return (
    <div className="pt-16">
      <SEO
        title={title}
        description={description.slice(0, 160)}
        canonical={buildCanonicalUrl(`/project/${project.id}`)}
        ogImage={imageSrc}
        lang={i18n.resolvedLanguage ?? i18n.language}
      />
      <section className="section-padding bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/projects"
              className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('projects.back')}
            </Link>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 max-w-4xl">
              {title}
            </h1>

            <div className="flex flex-wrap gap-4 text-gray-600">
              {project.clientName && (
                <div className="flex items-center text-sm">
                  <Building2 className="w-4 h-4 mr-2 text-primary-600" />
                  {project.clientName}
                </div>
              )}
              {project.completedDate && (
                <div className="flex items-center text-sm">
                  <Calendar className="w-4 h-4 mr-2 text-primary-600" />
                  {new Date(project.completedDate).toLocaleDateString(dateLocale)}
                </div>
              )}
              {industryType && (
                <div className="flex items-center text-sm">
                  <Tag className="w-4 h-4 mr-2 text-primary-600" />
                  {industryType}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid gap-8 lg:grid-cols-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-2 space-y-8"
            >
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={title}
                  className="w-full rounded-2xl shadow-lg object-cover max-h-96"
                />
              ) : (
                <div className="w-full h-64 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                  <FolderKanban className="w-20 h-20 text-white" />
                </div>
              )}

              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t('projects.descriptionTitle')}
                </h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {description}
                </p>
              </div>

              <button
                  type="button"
                  onClick={() => window.location.href = '/contact'}
                  className="btn-primary flex items-center justify-center"
                >
                  {t('projects.apply')}
                  <Send className="w-4 h-4 ml-2" />
                </button>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  )
}

export default ProjectsView
